import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from '../lib/dates';
import { CalendarOff, Trash2 } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { AbsenceKind, InstructorAbsence } from '../types';
import { Badge, Button, Callout, Card, ConfirmDialog, Segmented, Switch, TextField, useToast } from '../components/charte';

// Congés et absences (18/10/2026) : le moniteur déclare lui-même une période d'indisponibilité. Elle ferme son
// planning ; ses leçons d'élève déjà prévues sur la période partent au secrétariat (une tâche par élève, avec
// les moniteurs libres proposés par Permio AI), qui les replace. Les heures sont celles de l'auto-école.

const fmtPeriod = (a: InstructorAbsence) => {
  const start = dayjs(a.startsAt);
  // Une absence « journées entières » finit à minuit : on affiche la veille.
  const end = dayjs(a.endsAt);
  const wholeDays = start.format('HH:mm') === '00:00' && end.format('HH:mm') === '00:00';
  if (wholeDays) {
    const last = end.subtract(1, 'day');
    return last.isSame(start, 'day') ? `Le ${start.format('DD/MM/YYYY')}` : `Du ${start.format('DD/MM/YYYY')} au ${last.format('DD/MM/YYYY')}`;
  }
  return start.isSame(end, 'day')
    ? `Le ${start.format('DD/MM/YYYY')} de ${start.format('HH:mm')} à ${end.format('HH:mm')}`
    : `Du ${start.format('DD/MM/YYYY HH:mm')} au ${end.format('DD/MM/YYYY HH:mm')}`;
};

function DeclareForm() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const today = dayjs().format('YYYY-MM-DD');
  const [kind, setKind] = useState<AbsenceKind>('leave');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [wholeDays, setWholeDays] = useState(true);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Une même saisie renvoyée (réseau coupé) ne crée pas deux absences.
  const request = useRef({ signature: '', id: '' });

  const period = useMemo(() => {
    if (!from || !to) return null;
    const startsAt = wholeDays ? `${from}T00:00` : `${from}T${startTime}`;
    const endsAt = wholeDays ? `${dayjs(to).add(1, 'day').format('YYYY-MM-DD')}T00:00` : `${to}T${endTime}`;
    return { startsAt, endsAt };
  }, [from, to, wholeDays, startTime, endTime]);

  const declare = useMutation({
    mutationFn: () => {
      const payload = { kind, ...period!, note: note.trim() || undefined };
      const signature = JSON.stringify(payload);
      if (request.current.signature !== signature) request.current = { signature, id: crypto.randomUUID() };
      return instructorApi.declareAbsence({ requestId: request.current.id, ...payload });
    },
    onSuccess: (result) => {
      request.current = { signature: '', id: '' };
      void queryClient.invalidateQueries({ queryKey: ['absences'] });
      void queryClient.invalidateQueries({ queryKey: ['planning'] });
      void queryClient.invalidateQueries({ queryKey: ['today'] });
      setNote('');
      toast(
        result.lessons
          ? `Période enregistrée. ${result.lessons} leçon${result.lessons > 1 ? 's' : ''} transmise${result.lessons > 1 ? 's' : ''} au secrétariat pour être replacée${result.lessons > 1 ? 's' : ''}.`
          : 'Période enregistrée : votre planning est fermé sur ces dates.',
      );
    },
    onError: (e: Error) => setError(e.message || 'Enregistrement impossible.'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!period) return setError('Indiquez les dates.');
    if (dayjs(period.endsAt).isBefore(dayjs(period.startsAt)) || period.endsAt === period.startsAt)
      return setError('La fin doit suivre le début.');
    setError(null);
    declare.mutate();
  };

  return (
    <Card className="p-5 sm:p-6">
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <Segmented
          label="Nature de l’indisponibilité"
          value={kind}
          onChange={setKind}
          options={[
            { id: 'leave', label: 'Congés' },
            { id: 'absence', label: 'Absence' },
          ]}
        />
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <TextField label="Du" type="date" min={today} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="Au" type="date" min={from || today} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Switch checked={wholeDays} onChange={setWholeDays} label="Journées entières" />
        {!wholeDays && (
          <div className="grid grid-cols-2 gap-3">
            <TextField label="À partir de" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            <TextField label="Jusqu’à" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        )}
        <TextField
          label="Précision (facultatif)"
          maxLength={200}
          value={note}
          placeholder={kind === 'leave' ? 'Ex. vacances d’été' : 'Ex. rendez-vous médical'}
          onChange={(e) => setNote(e.target.value)}
        />
        <Callout tone="info">
          Votre planning est fermé sur cette période. Si des leçons y sont déjà prévues, le secrétariat est prévenu pour replacer vos élèves,
          avec les moniteurs libres proposés par Permio AI.
        </Callout>
        {error && (
          <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" icon={CalendarOff} loading={declare.isPending} className="h-11 sm:justify-self-start">
          Déclarer {kind === 'leave' ? 'mes congés' : 'mon absence'}
        </Button>
      </form>
    </Card>
  );
}

export default function AbsencesView() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['absences'], queryFn: instructorApi.getAbsences });
  const [cancelling, setCancelling] = useState<InstructorAbsence | null>(null);
  const cancel = useMutation({
    mutationFn: (a: InstructorAbsence) => instructorApi.cancelAbsence(a.id),
    onSuccess: () => {
      setCancelling(null);
      void queryClient.invalidateQueries({ queryKey: ['absences'] });
      void queryClient.invalidateQueries({ queryKey: ['planning'] });
      toast('Période retirée : votre planning est rouvert.');
    },
  });
  const now = Date.now();
  const active = (Array.isArray(data) ? data : []).filter((a) => !a.releasedAt);

  return (
    <section className="mx-auto grid max-w-3xl gap-5">
      <header>
        <h1 className="text-title-md text-heading sm:text-title-lg">Congés et absences</h1>
        <p className="mt-1 text-sm text-secondary">Déclarez vos congés ou une absence : votre planning est fermé sur ces dates.</p>
      </header>

      <DeclareForm />

      <div className="grid gap-3">
        <h2 className="text-title-sm text-heading">Mes périodes</h2>
        {isLoading && <p className="text-secondary">Chargement…</p>}
        {isError && (
          <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            Impossible de charger vos périodes : {(error as Error).message}
          </p>
        )}
        {data && active.length === 0 && <p className="text-sm text-secondary">Aucune période déclarée.</p>}
        <ul className="grid gap-2">
          {active.map((a) => {
            const started = Date.parse(a.startsAt) <= now;
            const ended = Date.parse(a.endsAt) <= now;
            return (
              <li key={a.id}>
                <Card className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-heading">{fmtPeriod(a)}</p>
                    <p className="text-[13px] text-secondary">{a.reason}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {ended ? <Badge tone="mute">Terminée</Badge> : started ? <Badge tone="todo">En cours</Badge> : <Badge tone="ok">À venir</Badge>}
                      {a.lessons > 0 && (
                        <Badge tone="todo">
                          {a.lessons} leçon{a.lessons > 1 ? 's' : ''} confiée{a.lessons > 1 ? 's' : ''} au secrétariat
                        </Badge>
                      )}
                    </div>
                  </div>
                  {a.declaredByMe && !started && (
                    <Button size="sm" variant="ghost" icon={Trash2} onClick={() => setCancelling(a)}>
                      Retirer
                    </Button>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      </div>

      <ConfirmDialog
        open={!!cancelling}
        title="Retirer cette période ?"
        confirmLabel="Retirer"
        tone="danger"
        pending={cancel.isPending}
        error={cancel.isError ? (cancel.error as Error).message : undefined}
        onConfirm={() => cancelling && cancel.mutate(cancelling)}
        onCancel={() => setCancelling(null)}
      >
        Votre planning est rouvert sur ces dates. Les leçons déjà replacées par le secrétariat le restent.
      </ConfirmDialog>
    </section>
  );
}
